import { blend, parseHex } from "@/pages/builders/app/appThemePalette";
import { direction, type DirectionId } from "./directions";

/**
 * Email/Doc-facing view of a design direction — the palette expanded into the
 * derived tints a baked-HTML preset needs, plus the font stacks that survive
 * email clients and the openhtmltopdf export.
 *
 * WHY: the email/doc preset corpus hand-rolled per-file hex constants that
 * duplicate (and drift from) the registry. Presets swap those consts for a
 * `directionTheme(id)` lookup so a direction retune propagates everywhere.
 *
 * All hex output is LOWERCASED: the preset consts it replaces are lowercase,
 * so a swap where the values already match is byte-identical in the rendered
 * HTML (the colour-masked structure snapshots stay green either way — this
 * keeps the raw git diff quiet too).
 *
 * Every derived tint is an OPAQUE hex blend, not rgba(): Outlook's Word engine
 * and openhtmltopdf are unreliable with translucent colour; pre-blending over
 * the direction background is safe everywhere. `rgbaOf` is exported for the
 * rare intentional overlay (screens over photos), never for text/border ink.
 */

export type DirectionTheme = {
  id: DirectionId;
  isDark: boolean;
  /** The registry 4-tuple, lowercased. */
  primary: string;
  accent: string;
  bg: string;
  text: string;
  /** Card surface — text ink washed 4% over bg (panel = 6%). */
  card: string;
  panel: string;
  /** Hairline rules — text ink 14% / 8% over bg, pre-blended. */
  hair: string;
  hairSoft: string;
  /** 10% primary wash over bg — chips, facts cells, quiet accents. */
  soft: string;
  /** Secondary ink ramp — text blended over bg at 70/55/40%. */
  ink70: string;
  ink55: string;
  ink40: string;
  /** Muted body ink (64%) — captions, footers. */
  muted: string;
  /** Email-safe font stacks for the direction's fontHead/fontBody. */
  headStack: string;
  bodyStack: string;
};

/** rgba() string for intentional translucent overlays only (never text ink —
 *  Outlook/openhtmltopdf handle opaque blends far more reliably). */
export const rgbaOf = (hex: string, alpha: number): string => {
  const c = parseHex(hex) ?? { r: 0, g: 0, b: 0 };
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${alpha})`;
};

/**
 * Registry family → email-safe CSS stack + the openhtmltopdf face that renders
 * it in the PDF export. `pdfEmbedded: false` marks faces NOT yet in the
 * backend embed set — until that ships, presets must lead their stack with an
 * embedded family (the stack below already falls back to one), and the doc
 * builder should not advertise the face as PDF-faithful. `pdfFace` of such an
 * entry is the PLANNED family; what the PDF embeds TODAY for any stack is
 * {@link pdfEmbeddedFace} (Fraunces → Gelasio through its Georgia fallback,
 * Hanken Grotesk → Inter, Cormorant Garamond and Montserrat → nothing).
 *
 * Keep in lockstep with src/lib/utils/builder-fonts.ts FACES (the canvas/PDF
 * parity injector) and the backend openhtmltopdf font set.
 */
export type FontMapping = {
  /** Email stack: licensed/true face first, graceful web-safe fallbacks. */
  stack: string;
  /** CSS family name the PDF renderer embeds (or will embed) for this look. */
  pdfFace: string;
  /** True iff pdfFace is in the backend openhtmltopdf embed set TODAY. */
  pdfEmbedded: boolean;
};

export const EMAIL_FONT_STACKS: Record<string, FontMapping> = {
  // ── embedded today (builder-fonts FACES ↔ backend embed set) ─────────────
  Georgia: { stack: "Georgia, 'Times New Roman', serif", pdfFace: "Georgia", pdfEmbedded: true },
  Futura: { stack: "Futura, Jost, 'Century Gothic', 'Trebuchet MS', sans-serif", pdfFace: "Futura", pdfEmbedded: true },
  Inter: { stack: "Inter, Helvetica, Arial, sans-serif", pdfFace: "Inter", pdfEmbedded: true },
  Poppins: { stack: "Poppins, 'Trebuchet MS', Arial, sans-serif", pdfFace: "Poppins", pdfEmbedded: true },
  Lora: { stack: "Lora, Georgia, serif", pdfFace: "Lora", pdfEmbedded: true },
  "IBM Plex Mono": { stack: "'IBM Plex Mono', 'Courier New', monospace", pdfFace: "IBM Plex Mono", pdfEmbedded: true },
  "Playfair Display": { stack: "'Playfair Display', Didot, Georgia, serif", pdfFace: "Playfair Display", pdfEmbedded: true },
  // ── substitutes onto an embedded face ────────────────────────────────────
  Didot: { stack: "Didot, 'Playfair Display', Georgia, serif", pdfFace: "Playfair Display", pdfEmbedded: true },
  "Bodoni 72": { stack: "'Bodoni 72', 'Bodoni MT', 'Playfair Display', Georgia, serif", pdfFace: "Playfair Display", pdfEmbedded: true },
  "Adobe Caslon Pro": { stack: "'Adobe Caslon Pro', Lora, Georgia, serif", pdfFace: "Lora", pdfEmbedded: true },
  "Helvetica Neue": { stack: "'Helvetica Neue', Helvetica, Inter, Arial, sans-serif", pdfFace: "Inter", pdfEmbedded: true },
  "Neue Haas Grotesk": { stack: "'Neue Haas Grotesk', 'Helvetica Neue', Inter, Helvetica, Arial, sans-serif", pdfFace: "Inter", pdfEmbedded: true },
  "Söhne": { stack: "Söhne, Inter, Helvetica, Arial, sans-serif", pdfFace: "Inter", pdfEmbedded: true },
  "Work Sans": { stack: "'Work Sans', Inter, Helvetica, Arial, sans-serif", pdfFace: "Inter", pdfEmbedded: true },
  Figtree: { stack: "Figtree, Poppins, 'Trebuchet MS', Arial, sans-serif", pdfFace: "Poppins", pdfEmbedded: true },
  "Bricolage Grotesque": { stack: "'Bricolage Grotesque', Poppins, 'Trebuchet MS', Arial, sans-serif", pdfFace: "Poppins", pdfEmbedded: true },
  // ── pending backend embed (E-WS0 cross-repo item) ────────────────────────
  "Cormorant Garamond": { stack: "'Cormorant Garamond', Garamond, 'Times New Roman', serif", pdfFace: "Cormorant Garamond", pdfEmbedded: false },
  Montserrat: { stack: "Montserrat, 'Trebuchet MS', Arial, sans-serif", pdfFace: "Montserrat", pdfEmbedded: false },
  Fraunces: { stack: "Fraunces, Georgia, serif", pdfFace: "Fraunces", pdfEmbedded: false },
  "Hanken Grotesk": { stack: "'Hanken Grotesk', Inter, Arial, sans-serif", pdfFace: "Hanken Grotesk", pdfEmbedded: false },
};

/** Stack lookup with a loud throw — a registry family missing here is a
 *  programming error caught by the test, never a silent serif fallback. */
export function emailFontStack(family: string): FontMapping {
  const m = EMAIL_FONT_STACKS[family];
  if (!m) throw new Error(`No email font mapping for registry family: ${family}`);
  return m;
}

/**
 * CSS family → the real name of the bundled face the PDF renderer EMBEDS
 * under it. A mirror of `DocumentPdfExporter.registerFonts` in main-backend
 * (`builder.useFont(font("/fonts/Jost-Regular.ttf"), "Futura", …)`), which is
 * the only place a document PDF gets a typeface from; the canvas injector
 * `src/lib/utils/builder-fonts.ts` FACES serves the same faces under the same
 * names (minus "Arial Black"). Both are pinned in `directionTheme.test.ts`.
 *
 * DejaVu Sans is registered too, as the FALLBACK_FINAL glyph net; no stack
 * names it, so it is not a face a picker could show.
 */
export const PDF_REGISTERED_FACES: Readonly<Record<string, string>> = {
  Futura: "Jost",
  "Arial Black": "Jost",
  Inter: "Inter",
  Poppins: "Poppins",
  "IBM Plex Mono": "IBM Plex Mono",
  Georgia: "Gelasio",
  "Playfair Display": "Playfair Display",
  Lora: "Lora",
};

/**
 * The face a document PDF embeds for a CSS font stack, or null when it embeds
 * none and prints a PDF core font instead (Times / Helvetica — not embedded,
 * the reader's own).
 *
 * openhtmltopdf collects the REGISTERED fonts over the whole family list
 * first and only then the base-14 built-ins (`PdfBoxFontResolver.resolveFont`:
 * `_suppliedFonts` for every family, then `_builtinFonts`), so the first
 * registered family in the stack wins wherever it stands:
 * `'Helvetica Neue', Helvetica, Inter, Arial, sans-serif` embeds Inter, not
 * core Helvetica. Family names match CASE-SENSITIVELY — `georgia` is not
 * `Georgia` there, although a browser treats them alike. Measured by
 * rendering every registry stack through the exporter (audit R1 of Kanzlei
 * 05.10., K2-09); what a picker for the DOCUMENT surface shows is this, and
 * the stack's own family name where it is null.
 */
export function pdfEmbeddedFace(stack: string): string | null {
  for (const part of stack.split(",")) {
    const family = part.trim().replace(/^(['"])(.*)\1$/, "$2");
    // hasOwn: a family called "constructor" must not hit Object.prototype.
    const face = Object.hasOwn(PDF_REGISTERED_FACES, family)
      ? PDF_REGISTERED_FACES[family]
      : undefined;
    if (face) return face;
  }
  return null;
}

const lower = (hex: string): string => hex.toLowerCase();

const cache = new Map<DirectionId, DirectionTheme>();

export function directionTheme(id: DirectionId): DirectionTheme {
  const hit = cache.get(id);
  if (hit) return hit;
  const d = direction(id);
  const bg = lower(d.background);
  const text = lower(d.text);
  const t: DirectionTheme = {
    id,
    isDark: d.isDark,
    primary: lower(d.primary),
    accent: lower(d.accent),
    bg,
    text,
    card: lower(blend(text, 0.04, bg)),
    panel: lower(blend(text, 0.06, bg)),
    hair: lower(blend(text, 0.14, bg)),
    hairSoft: lower(blend(text, 0.08, bg)),
    soft: lower(blend(d.primary, 0.1, bg)),
    ink70: lower(blend(text, 0.7, bg)),
    ink55: lower(blend(text, 0.55, bg)),
    ink40: lower(blend(text, 0.4, bg)),
    muted: lower(blend(text, 0.64, bg)),
    headStack: emailFontStack(d.fontHead).stack,
    bodyStack: emailFontStack(d.fontBody).stack,
  };
  // The cached instance is SHARED by every preset module — freeze it so a
  // stray mutation can't silently retheme the whole corpus.
  Object.freeze(t);
  cache.set(id, t);
  return t;
}
